import { useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { fetchReceipt, downloadReceiptPdf, createBillingPortalSession, syncSubscription } from "../lib/api";
import { useAuth } from "../lib/auth";
import "./payment-success-receipt.css";

export default function ReceiptPage() {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const [state, setState] = useState({ status: "loading", receipt: null });
  const tries = useRef(0);

  // Independently of the receipt build, reconcile Pro from Stripe so a paying
  // user is never left on Free even if receipt generation hiccups.
  useEffect(() => {
    syncSubscription()
      .then((r) => { if (r?.is_pro && refresh) refresh(); })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!sessionId) {
      setState({ status: "error", receipt: null });
      return;
    }
    let cancelled = false;
    const load = async () => {
      try {
        const data = await fetchReceipt(sessionId);
        if (!cancelled) setState({ status: "ready", receipt: data });
      } catch {
        tries.current += 1;
        if (tries.current >= 10) {
          if (!cancelled) setState({ status: "error", receipt: null });
          return;
        }
        if (!cancelled) setTimeout(load, 2000);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [sessionId]);

  const onDownload = async (receiptNo) => {
    try {
      await downloadReceiptPdf(receiptNo);
    } catch {
      toast.error("Couldn't download the PDF", { description: "Please try again in a moment." });
    }
  };

  const onManage = async () => {
    try {
      const { url } = await createBillingPortalSession();
      if (url) {
        window.location.href = url;
        return;
      }
      throw new Error("No portal URL");
    } catch (e) {
      const msg = e?.response?.data?.detail;
      toast.error("Couldn't open billing portal", {
        description: typeof msg === "string" ? msg : "Please try again in a moment.",
      });
    }
  };

  if (state.status === "loading") {
    return (
      <main className="bs-payment-page">
        <div className="bs-state-card" role="status" data-testid="receipt-loading">
          Verifying your payment…
        </div>
      </main>
    );
  }

  if (state.status === "error") {
    return (
      <main className="bs-payment-page">
        <div className="bs-state-card bs-state-error" role="alert" data-testid="receipt-error">
          We couldn't verify the receipt yet. Please refresh in a moment or contact hello@blendsafe.com.
        </div>
      </main>
    );
  }

  const r = state.receipt;
  return (
    <main className="bs-payment-page" data-testid="receipt-page">
      <section className="bs-payment-card">
        <header className="bs-payment-header">
          <div>
            <img
              src="/brand/blendsafe-logo-full.png"
              alt="BlendSafe — Skincare ✕ Medication Safety"
              className="bs-receipt-logo"
              data-testid="receipt-logo"
            />
          </div>
          <div className="bs-receipt-meta">
            <strong data-testid="receipt-number">Receipt {r.receiptNumber}</strong>
            <span data-testid="receipt-date">{r.receiptDate}</span>
          </div>
        </header>

        <div className="bs-success-banner" role="status">
          <span className="bs-success-check" aria-hidden="true">✓</span>
          <div>
            <p className="bs-eyebrow">Payment successful</p>
            <h1 id="payment-success-title">You're all set.</h1>
            <p data-testid="receipt-access-message">{r.accessMessage}</p>
          </div>
        </div>

        <div className="bs-payment-grid">
          <div className="bs-receipt-content">
            <div className="bs-billed-to">
              <span>Billed to</span>
              <strong data-testid="receipt-billed-to">{r.customerEmail}</strong>
            </div>

            <div className="bs-line-item" role="table" aria-label="Receipt item">
              <div className="bs-line-header" role="row">
                <span role="columnheader">Item</span>
                <span role="columnheader">Period</span>
                <span role="columnheader">Amount</span>
              </div>
              <div className="bs-line-body" role="row">
                <strong role="cell" data-testid="receipt-plan">{r.planName}</strong>
                <span role="cell" data-testid="receipt-period">{r.billingPeriod}</span>
                <strong role="cell" data-testid="receipt-amount">{r.amount}</strong>
              </div>
            </div>

            <div className="bs-totals">
              <div><span>Tax</span><span data-testid="receipt-tax">{r.tax}</span></div>
              <div className="bs-total"><strong>Total</strong><strong data-testid="receipt-total">{r.total}</strong></div>
            </div>

            <div className="bs-payment-method">
              <strong data-testid="receipt-paid-with">
                {r.cardLast4 ? `Paid with ${r.cardBrand || "card"} ····${r.cardLast4}` : "Paid online"}
              </strong>
              <span>Appears on your statement as {r.statementDescriptor}</span>
            </div>

            <div className="bs-actions">
              <button
                className="bs-button bs-button-primary"
                data-testid="receipt-download-pdf"
                onClick={() => onDownload(r.receiptNo)}
              >
                Download PDF receipt &nbsp;→
              </button>
              <button
                className="bs-button bs-button-secondary"
                data-testid="receipt-manage-subscription"
                onClick={onManage}
              >
                Manage subscription &nbsp;→
              </button>
              <button
                className="bs-text-link"
                data-testid="receipt-go-routine"
                onClick={() => navigate("/routine")}
              >
                Go to my routine →
              </button>
            </div>
          </div>

          <aside className="bs-brand-panel" aria-label="BlendSafe skin-safety illustration">
            <div className="bs-shield-chip"><span aria-hidden="true">♡</span> Skin-safety first</div>
            <img src="/brand/blendsafe-skin-safety-woman.png" alt="Woman calmly checking her skin" />
          </aside>
        </div>

        <footer className="bs-payment-footer">
          <span>{r.companyLine}</span>
          <span>Information from the FDA label and published sources. Not medical advice.</span>
        </footer>
      </section>
    </main>
  );
}
